/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import { useState, useCallback } from 'react';
import { showErrorMessage } from '../../../../../../utils';
import {
  DECISION_REASON_FIELD,
  V2Type,
  getCredentialKey,
  getDecisionReasonServerError,
} from '../credentialV2Utils';
import { useCredentialNumber } from './useCredentialNumber';
import { useCredentialArtifact } from './useCredentialArtifact';
import { useCredentialNpi } from './useCredentialNpi';
import { useCredentialReference } from './useCredentialReference';

const findOriginalCred = (authorization, cred) => {
  const origCreds = authorization?.credentials || [];
  if (cred.id) return origCreds.find((c) => c?.id === cred.id);
  return origCreds.find((c) => c?.credential === cred.credential);
};

const hasCredChanged = (current, original) => {
  if (!original) return true;
  return JSON.stringify(current) !== JSON.stringify(original);
};

/** `{ [credentialId]: message }` for the save failures that are the CS reason's 400. */
const collectDecisionReasonErrors = (saveResult) => {
  const errors = {};
  (saveResult?.failures || []).forEach(({ credentialId, reason }) => {
    const message = getDecisionReasonServerError(reason);
    if (credentialId && message) errors[credentialId] = message;
  });
  return errors;
};

export const useCredentialForm = ({ authorization, onSave }) => {
  const [editMode, setEditMode] = useState(false);
  const [localAuth, setLocalAuth] = useState(null);
  const [attested, setAttested] = useState(false);
  const [noExpirations, setNoExpirations] = useState({});
  const [showAllErrors, setShowAllErrors] = useState(false);
  // The CS reason's server-side rejections, by credential id, until that reason is edited.
  const [reasonServerErrors, setReasonServerErrors] = useState({});

  const numberValidation = useCredentialNumber();
  const artifactValidation = useCredentialArtifact();
  const npiValidation = useCredentialNpi();
  const referenceValidation = useCredentialReference();

  const auth = editMode && localAuth ? localAuth : authorization;
  const credentials = auth?.credentials || [];
  const confirmedName = auth?.confirmed_name || null;

  const handleEdit = useCallback(() => {
    setLocalAuth(structuredClone(authorization));
    setAttested(false);
    setNoExpirations({});
    setShowAllErrors(false);
    setReasonServerErrors({});
    setEditMode(true);
  }, [authorization]);

  const handleCancel = useCallback(() => {
    setLocalAuth(null);
    setAttested(false);
    setNoExpirations({});
    setShowAllErrors(false);
    setReasonServerErrors({});
    setEditMode(false);
  }, []);

  // --- credential updaters ---

  const updateCredentialById = useCallback((credId, updater) => {
    setLocalAuth((prev) => {
      if (!prev) return prev;
      const newAuth = structuredClone(prev);
      const cred = newAuth.credentials?.find((c) => c?.id === credId);
      if (cred) updater(cred);
      return newAuth;
    });
  }, []);

  const updateCredentialByCredEnum = useCallback((credEnum, updater) => {
    setLocalAuth((prev) => {
      if (!prev) return prev;
      const newAuth = structuredClone(prev);
      const cred = newAuth.credentials?.find((c) => c?.credential === credEnum);
      if (cred) updater(cred);
      return newAuth;
    });
  }, []);

  const updateReferenceByIndex = useCallback((index, updater) => {
    setLocalAuth((prev) => {
      if (!prev) return prev;
      const newAuth = structuredClone(prev);
      const refs = newAuth.credentials?.filter((c) => c?.type === V2Type.Reference) || [];
      if (refs[index]) updater(refs[index]);
      return newAuth;
    });
  }, []);

  const updateCred = useCallback(
    (cred, updater) => {
      if (cred.id) {
        updateCredentialById(cred.id, updater);
      } else {
        updateCredentialByCredEnum(cred.credential, updater);
      }
    },
    [updateCredentialById, updateCredentialByCredEnum],
  );

  const updateDecisionReason = useCallback(
    (cred, value) => {
      updateCred(cred, (c) => {
        c[DECISION_REASON_FIELD] = value;
      });
      setReasonServerErrors((prev) => {
        if (!(cred.id in prev)) return prev;
        const { [cred.id]: _cleared, ...rest } = prev;
        return rest;
      });
    },
    [updateCred],
  );

  // --- confirmed-name (CS name change) updater ---

  const updateConfirmedName = useCallback((updater) => {
    setLocalAuth((prev) => {
      if (!prev) return prev;
      const cn = { ...(prev.confirmed_name || {}) };
      cn._files = [...(cn._files || [])];
      updater(cn);
      return { ...prev, confirmed_name: cn };
    });
  }, []);

  // --- no-expiration helpers ---

  const setNoExpiration = useCallback(
    (cred, checked) => {
      const key = getCredentialKey(cred);
      setNoExpirations((prev) => ({ ...prev, [key]: checked }));
      if (checked) {
        updateCred(cred, (c) => {
          c.expired_at = null;
        });
      }
    },
    [updateCred],
  );

  const getNoExpiration = useCallback(
    (cred) => {
      const key = getCredentialKey(cred);
      return key in noExpirations ? noExpirations[key] : !cred?.expired_at;
    },
    [noExpirations],
  );

  // --- error accessors ---

  const getFieldError = useCallback(
    (cred, field) => {
      if (field === DECISION_REASON_FIELD && reasonServerErrors[cred?.id]) {
        return reasonServerErrors[cred.id];
      }

      const original = findOriginalCred(authorization, cred);
      const changed = hasCredChanged(cred, original);

      // No change from original → no errors to show
      if (!changed) return null;

      switch (cred?.type) {
        case V2Type.Number: {
          const errors = showAllErrors
            ? numberValidation.validateAll([cred])[getCredentialKey(cred)] || {}
            : numberValidation.getErrors(cred, original);
          return errors[field] || null;
        }
        case V2Type.Artifact: {
          const noExpiration = getNoExpiration(cred);
          const errors = showAllErrors
            ? artifactValidation.validateAll([cred], noExpirations, getCredentialKey)[
                getCredentialKey(cred)
              ] || {}
            : artifactValidation.getErrors(cred, original, noExpiration);
          return errors[field] || null;
        }
        case V2Type.Npi: {
          const errors = showAllErrors
            ? npiValidation.validateAll([cred])[getCredentialKey(cred)] || {}
            : npiValidation.getErrors(cred, original);
          return errors[field] || null;
        }
        case V2Type.Reference: {
          const errors = showAllErrors
            ? referenceValidation.validateAll([cred])[getCredentialKey(cred)] || {}
            : referenceValidation.getErrors(cred, original);
          return errors[field] || null;
        }
        default:
          return null;
      }
    },
    // eslint-disable-next-line max-len
    [
      authorization,
      showAllErrors,
      reasonServerErrors,
      noExpirations,
      getNoExpiration,
      numberValidation,
      artifactValidation,
      npiValidation,
      referenceValidation,
    ],
  );

  // --- save: only validate & send changed credentials ---

  const handleSave = useCallback(async () => {
    const creds = localAuth?.credentials || [];

    // Filter to only credentials that have actually changed
    const changedCreds = creds.filter((cred) => {
      const original = findOriginalCred(authorization, cred);
      return hasCredChanged(cred, original);
    });

    // --- confirmed-name change detection (per-state CS name change) ---
    const origCn = authorization?.confirmed_name || {};
    const curCn = localAuth?.confirmed_name || {};
    const firstName = (curCn.first_name || '').trim();
    const lastName = (curCn.last_name || '').trim();
    const namesChanged =
      firstName !== (origCn.first_name || '').trim() ||
      lastName !== (origCn.last_name || '').trim();

    // Prefetched docs are already _local, so compare the file-id sets: a change means
    // the user actually added or removed a document (names-only edits leave this equal).
    const fileIds = (list) =>
      (list || [])
        .map((f) => f.file_id || f.url)
        .sort()
        .join(',');
    const workingFiles = curCn._files || [];
    const confirmedFilesChanged = fileIds(workingFiles) !== fileIds(origCn._files);

    const confirmedNameChanged = namesChanged || confirmedFilesChanged;

    // First/last name must be set together (or both cleared).
    if (namesChanged && !!firstName !== !!lastName) {
      showErrorMessage({ message: 'Enter both first and last name, or leave both blank.' });
      return false;
    }

    // Nothing changed → just close edit mode, no API call
    if (changedCreds.length === 0 && !confirmedNameChanged) {
      setEditMode(false);
      return true;
    }

    // Validate only the changed credentials
    const numberCreds = changedCreds.filter((c) => c?.type === V2Type.Number);
    const artifactCreds = changedCreds.filter((c) => c?.type === V2Type.Artifact);
    const npiCreds = changedCreds.filter((c) => c?.type === V2Type.Npi);
    const refCreds = changedCreds.filter((c) => c?.type === V2Type.Reference);

    const numberErrors = numberValidation.validateAll(numberCreds);
    const artifactErrors = artifactValidation.validateAll(
      artifactCreds,
      noExpirations,
      getCredentialKey,
    );
    const npiErrors = npiValidation.validateAll(npiCreds);
    const refErrors = referenceValidation.validateAll(refCreds);

    const hasErrors =
      Object.keys(numberErrors).length > 0 ||
      Object.keys(artifactErrors).length > 0 ||
      Object.keys(npiErrors).length > 0 ||
      Object.keys(refErrors).length > 0;

    if (hasErrors) {
      setShowAllErrors(true);
      showErrorMessage({ message: 'Please fix the highlighted errors before saving.' });

      // Scroll to the first error field after re-render
      setTimeout(() => {
        const firstError = document.querySelector('[data-field-error="true"]');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);

      return false;
    }

    // Build payload with only changed credentials
    const payload = {
      ...localAuth,
      credentials: changedCreds,
      _confirmedNameChanged: confirmedNameChanged,
      _confirmedFilesChanged: confirmedFilesChanged,
    };

    const saveResult = onSave ? await onSave(payload) : undefined;

    // A rejected CS reason stays editable with the server's message under it; every
    // other failure keeps the save's existing toast and closes the form as before.
    const reasonErrors = collectDecisionReasonErrors(saveResult);
    if (Object.keys(reasonErrors).length > 0) {
      setReasonServerErrors(reasonErrors);
      return false;
    }

    setEditMode(false);
    return true;
  }, [
    localAuth,
    authorization,
    noExpirations,
    onSave,
    numberValidation,
    artifactValidation,
    npiValidation,
    referenceValidation,
  ]);

  return {
    editMode,
    auth,
    credentials,
    confirmedName,
    attested,
    setAttested,
    handleEdit,
    handleCancel,
    handleSave,
    updateCred,
    updateDecisionReason,
    updateConfirmedName,
    updateReferenceByIndex,
    getFieldError,
    setNoExpiration,
    getNoExpiration,
  };
};
